import { css, type Theme } from '@emotion/react';
import styled from '@emotion/styled';

import { generateResponsiveStyles } from '@hrworks/design-system/functions';
import { lineClamp, mq, overflowEllipsis, overflowHyphens, shouldForwardProp } from '@hrworks/design-system/utilities';
import type { Color, Typography } from '@hrworks/sui-shared/types';

import type { TextProps as _TextProps } from './Text.types';

const interactiveTextStyles = (theme: Theme) =>
  css({
    cursor: 'pointer',
    textDecoration: 'underline',
    ':focus-visible': {
      outline: `2px solid ${theme.eclipseTier2Color.border.focus}`,
    },
    [mq.supportsHover]: {
      ':not(:disabled):hover': {
        textDecoration: 'none',
      },
    },
  });

type TextProps = {
  $variant: _TextProps['variant'];
  $hover: _TextProps['hover'];
  $width: _TextProps['width'];
  $color: _TextProps['color'];
  $underlined: _TextProps['underlined'];
  $italic: _TextProps['italic'];
  $textAlign: _TextProps['textAlign'];
  $typography?: Typography;
  $href: _TextProps['href'];
  $overflowBehaviour: NonNullable<_TextProps['overflowBehaviour']>;
  $lineClamp: _TextProps['lineClamp'];
};

const colorMap: Record<Color, (theme: Theme) => string> = {
  primary: (theme) => theme.eclipseTier2Color.text.brand.default,
  secondary: (theme) => theme.eclipseTier2Color.text.subtle,
  warning: (theme) => theme.eclipseTier2Color.text.warning.default,
  success: (theme) => theme.eclipseTier2Color.text.success.default,
  danger: (theme) => theme.eclipseTier2Color.text.error.default,
  info: (theme) => theme.eclipseTier2Color.text.info.default,
};

const Text = styled('span', {
  shouldForwardProp: (prop) => prop !== 'as' && shouldForwardProp(prop),
})<TextProps>(
  ({
    theme,
    $href,
    $textAlign,
    $overflowBehaviour,
    $lineClamp,
    $typography,
    $width,
    $color,
    $italic,
    $underlined,
    $variant,
    $hover,
  }) => [
    $href && interactiveTextStyles(theme),
    $textAlign &&
      generateResponsiveStyles({
        value: $textAlign,
        cssProp: 'textAlign',
      }),
    $overflowBehaviour === 'ellipsis' && [overflowEllipsis, { display: 'block' }],
    $overflowBehaviour === 'break' && overflowHyphens,
    $lineClamp && lineClamp($lineClamp),
    {
      transition: `opacity ${theme.variables.animationDuration.normal}`,
      color: 'currentcolor',

      ...($typography && theme.eclipseTier2Typography[$typography]),

      ...($width === 'full' && {
        display: 'block',
        width: '100%',
      }),
      ...($color && {
        color: colorMap[$color](theme),
      }),
      ...($italic && {
        fontStyle: 'italic',
      }),
      ...($underlined && {
        textDecoration: 'underline',
      }),
      ...($variant === 'subtle' && {
        opacity: theme.variables.opacity.medium,
      }),
      [mq.supportsHover]: {
        ...($hover && {
          ':hover': {
            opacity: $variant === 'subtle' ? theme.variables.opacity.high : theme.variables.opacity.medium,
          },
        }),
      },
    },
  ],
);

export const S = {
  interactiveTextStyles,
  Text,
} as const;
